import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PAGE_POSITIONS } from 'types';
import type { AffiliateStockListing, CapiProductPage } from 'types/capi/pdp';
import { fromProductDetailPage } from 'types/TrackableProduct';

import HiddenIcon from '~/assets/icons/Hidden';
import AffiliateProductLink from '~/components/AffiliateProductLink';
import type { Variant } from '~/components/Button';
import Modals from '~/components/Modals';
import Tracked from '~/components/Tracked';
import RegisterIntentEvent from '~/events/RegisterIntentEvent';
import usePageData from '~/hooks/usePageData';
import useRegion from '~/hooks/useRegion';
import useRootData from '~/hooks/useRootData';

import AffiliateLogo from './WhereToBuy/AffiliateLogo';

type Props = {
  buttonPosition: number;
  isDarkBg?: boolean;
  onClick?: () => void;
  pagePosition: PAGE_POSITIONS;
  selectedSize?: string;
  showAfffiliateLogo?: boolean;
  stockListing: AffiliateStockListing;
  variant?: Variant;
};

export const BuyNow = ({
  stockListing,
  buttonPosition,
  pagePosition,
  showAfffiliateLogo,
  selectedSize,
  variant = 'forest',
  onClick
}: Props) => {
  const product = usePageData<CapiProductPage>();
  const { lang } = useRegion();
  const { t } = useTranslation('pdp');

  const affiliateName =
    lang === 'en' && stockListing.affiliate.id === 'affiliate:marketplace'
      ? 'our marketplace'
      : stockListing.affiliate.name;

  return (
    <AffiliateProductLink
      urlTemplates={stockListing}
      affiliate={stockListing.affiliate}
      price={stockListing.price}
      product={fromProductDetailPage(product)}
      selectedSize={selectedSize}
      buttonPosition={buttonPosition}
      pagePosition={pagePosition}
      variant={variant}
      onClick={onClick}
    >
      <div className="flex h-8 items-center justify-center gap-2">
        <div className="flex items-center gap-2 text-base">
          {showAfffiliateLogo && (
            <div className="m-auto flex size-7 items-center rounded-md bg-white p-[0.05rem]">
              <AffiliateLogo
                src={stockListing.affiliate.logo}
                alt={stockListing.affiliate.name}
              />
            </div>
          )}
          <span>{t('buyFrom', { brand: affiliateName })}</span>
        </div>
      </div>
    </AffiliateProductLink>
  );
};

export const VisitSite = ({
  stockListing,
  buttonPosition,
  pagePosition,
  onClick
}: Props) => {
  const { t } = useTranslation();

  const product = usePageData<CapiProductPage>();

  return (
    <AffiliateProductLink
      product={product}
      buttonPosition={buttonPosition}
      pagePosition={pagePosition}
      urlTemplates={stockListing}
      affiliate={stockListing.affiliate}
      price={stockListing.price}
      variant="white"
      onClick={onClick}
    >
      <div className="flex h-full items-center justify-center gap-2">
        {t('visitSite')}
      </div>
    </AffiliateProductLink>
  );
};

export function Gated({
  pagePosition,
  showAfffiliateLogo,
  dynamicVariant
}: {
  dynamicVariant?: boolean;
  pagePosition: PAGE_POSITIONS;
  showAfffiliateLogo?: boolean;
}) {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const product = usePageData<CapiProductPage>();
  const { isBot } = useRootData();
  const { t } = useTranslation('pdp');

  async function handleClick(e: React.SyntheticEvent) {
    e.preventDefault();
    setShowLoginModal(true);

    return new RegisterIntentEvent({
      buttonName: 'GATED_RETAILER_CLICK',
      isBot,
      pagePosition
    }).send();
  }

  return (
    <div data-cy="stock-listing-buttons">
      <Tracked.Button
        name="GATED_RETAILER_CLICK"
        pagePosition={pagePosition}
        variant={!dynamicVariant ? 'forest' : 'green-cta'}
        onClick={handleClick}
      >
        <div className="flex w-full items-center justify-center gap-2">
          {showAfffiliateLogo && (
            <div className="rounded-lg bg-grey-tile p-1">
              <HiddenIcon />
            </div>
          )}
          <span>{t('gatedRetailerButton')}</span>
        </div>
      </Tracked.Button>
      {showLoginModal && (
        <Modals.SignUp
          modalType="pdpGate"
          hideAppDownloadPrompt
          onDismiss={() => setShowLoginModal(false)}
          image={product.gallery[0]}
        />
      )}
    </div>
  );
}

export default {
  BuyNow,
  Gated,
  VisitSite
};
